<template>
    <v-container>
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-card-title>
                        PokoSMS Gateway
                        <v-spacer></v-spacer>
                        <v-chip
                            :color="statusColor"
                            text-color="white"
                            small
                        >
                            {{ statusText }}
                        </v-chip>
                    </v-card-title>

                    <v-card-text>
                        <!-- Not configured warning -->
                        <v-alert v-if="!status.isConfigured" type="warning" class="mb-4">
                            PokoSMS Gateway nie jest skonfigurowany. Skontaktuj się z administratorem systemu.
                        </v-alert>

                        <!-- Integration toggle -->
                        <v-switch
                            v-model="integrationEnabled"
                            :label="integrationEnabled ? 'Integracja włączona' : 'Integracja wyłączona'"
                            :disabled="!status.isConfigured || loading"
                            @change="onToggleIntegration"
                            color="success"
                        ></v-switch>

                        <!-- Device section (visible when integration enabled) -->
                        <template v-if="status.enabled">
                            <v-divider class="my-4"></v-divider>

                            <h3 class="subtitle-1 mb-3">Urządzenie mobilne</h3>

                            <!-- No device -->
                            <template v-if="!status.deviceId">
                                <v-alert type="info" outlined class="mb-4">
                                    Brak sparowanego urządzenia. Kliknij przycisk poniżej aby wygenerować kod QR.
                                </v-alert>
                                <v-btn
                                    color="primary"
                                    @click="createDevice"
                                    :loading="creatingDevice"
                                >
                                    <v-icon left>mdi-qrcode</v-icon>
                                    Sparuj urządzenie
                                </v-btn>
                            </template>

                            <!-- Device pending -->
                            <template v-else-if="status.deviceStatus === 'Pending'">
                                <v-alert type="warning" outlined class="mb-4">
                                    <v-row align="center">
                                        <v-col class="grow">
                                            Urządzenie oczekuje na parowanie. Zeskanuj kod QR aplikacją PokoSMS.
                                        </v-col>
                                        <v-col class="shrink">
                                            <v-btn text small @click="refreshStatus" :loading="refreshing">
                                                <v-icon left small>mdi-refresh</v-icon>
                                                Odśwież
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-alert>

                                <!-- QR Code -->
                                <div v-if="qrCodeData" class="text-center mb-4">
                                    <img :src="qrCodeData" alt="QR Code" style="max-width: 300px;" />
                                    <p class="caption mt-2">Kod wygasa: {{ expiresAtFormatted }}</p>
                                </div>

                                <v-btn
                                    color="error"
                                    text
                                    @click="confirmDeleteDevice"
                                    :loading="deletingDevice"
                                >
                                    <v-icon left>mdi-delete</v-icon>
                                    Anuluj parowanie
                                </v-btn>
                            </template>

                            <!-- Device active -->
                            <template v-else-if="status.deviceStatus === 'Active'">
                                <v-alert type="success" outlined class="mb-4">
                                    <v-row align="center">
                                        <v-col class="grow">
                                            <strong>Urządzenie aktywne</strong><br>
                                            <small>ID: {{ status.deviceId }}</small>
                                        </v-col>
                                        <v-col class="shrink">
                                            <v-btn text small @click="refreshStatus" :loading="refreshing">
                                                <v-icon left small>mdi-refresh</v-icon>
                                                Odśwież
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-alert>

                                <v-btn
                                    color="error"
                                    text
                                    @click="confirmDeleteDevice"
                                    :loading="deletingDevice"
                                >
                                    <v-icon left>mdi-link-off</v-icon>
                                    Odparuj urządzenie
                                </v-btn>
                            </template>
                        </template>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Disable confirmation dialog -->
        <v-dialog v-model="showDisableDialog" max-width="500">
            <v-card>
                <v-card-title class="error--text">
                    <v-icon left color="error">mdi-alert</v-icon>
                    Wyłączenie integracji
                </v-card-title>
                <v-card-text>
                    <p class="mb-3">Wyłączenie integracji PokoSMS spowoduje:</p>
                    <ul class="mb-3">
                        <li>Usunięcie powiązanego urządzenia</li>
                        <li>Usunięcie wszystkich SMS-ów w kolejce</li>
                        <li>Usunięcie historii SMS-ów przychodzących</li>
                    </ul>
                    <p><strong>Czy na pewno chcesz kontynuować?</strong></p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="showDisableDialog = false">Anuluj</v-btn>
                    <v-btn color="error" @click="disableIntegration" :loading="disabling">
                        Wyłącz integrację
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete device confirmation dialog -->
        <v-dialog v-model="showDeleteDeviceDialog" max-width="400">
            <v-card>
                <v-card-title>
                    <v-icon left>mdi-link-off</v-icon>
                    Odparowanie urządzenia
                </v-card-title>
                <v-card-text>
                    <p>Czy na pewno chcesz odparować urządzenie?</p>
                    <p class="caption">Będziesz musiał ponownie zeskanować kod QR na telefonie.</p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="showDeleteDeviceDialog = false">Anuluj</v-btn>
                    <v-btn color="error" @click="deleteDevice" :loading="deletingDevice">
                        Odparuj
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { pokosmsService } from '@/services/pokosms.service'
import { mapActions } from 'vuex'

export default {
    name: "PokoSmsConfiguration",
    data() {
        return {
            status: {
                enabled: false,
                deviceId: null,
                deviceStatus: null,
                hasApiKey: false,
                isConfigured: false
            },
            loading: false,
            enabling: false,
            disabling: false,
            creatingDevice: false,
            deletingDevice: false,
            refreshing: false,
            qrCodeData: null,
            expiresAt: null,
            showDisableDialog: false,
            showDeleteDeviceDialog: false,
            integrationEnabled: false
        }
    },
    computed: {
        statusColor() {
            if (!this.status.isConfigured) return 'grey';
            if (!this.status.enabled) return 'grey';
            if (this.status.deviceStatus === 'Active') return 'success';
            if (this.status.deviceStatus === 'Pending') return 'warning';
            return 'info';
        },
        statusText() {
            if (!this.status.isConfigured) return 'Nie skonfigurowano';
            if (!this.status.enabled) return 'Wyłączone';
            if (this.status.deviceStatus === 'Active') return 'Aktywne';
            if (this.status.deviceStatus === 'Pending') return 'Oczekuje na parowanie';
            return 'Brak urządzenia';
        },
        expiresAtFormatted() {
            if (!this.expiresAt) return '';
            return new Date(this.expiresAt).toLocaleTimeString('pl-PL');
        }
    },
    mounted() {
        this.loadStatus();
    },
    methods: {
        ...mapActions("alert", ["error", "success"]),

        async loadStatus() {
            this.loading = true;
            try {
                const response = await pokosmsService.getStatus();
                this.status = response.data.result;
                this.integrationEnabled = this.status.enabled;
            } catch (err) {
                this.error('Błąd podczas pobierania statusu: ' + (err.response && err.response.data && err.response.data.error ? err.response.data.error.message : err.message));
            } finally {
                this.loading = false;
            }
        },

        onToggleIntegration(enabled) {
            if (enabled) {
                this.enableIntegration();
            } else {
                // Show confirmation dialog before disabling
                this.showDisableDialog = true;
                // Revert toggle until confirmed
                this.$nextTick(() => {
                    this.integrationEnabled = true;
                });
            }
        },

        async enableIntegration() {
            this.enabling = true;
            try {
                const response = await pokosmsService.enable();
                this.success(response.data.result.message);
                await this.loadStatus();
            } catch (err) {
                this.error('Błąd: ' + (err.response && err.response.data && err.response.data.error ? err.response.data.error.message : err.message));
                this.integrationEnabled = false;
            } finally {
                this.enabling = false;
            }
        },

        async disableIntegration() {
            this.disabling = true;
            try {
                const response = await pokosmsService.disable();
                this.success(response.data.result.message);
                this.showDisableDialog = false;
                this.qrCodeData = null;
                await this.loadStatus();
            } catch (err) {
                this.error('Błąd: ' + (err.response && err.response.data && err.response.data.error ? err.response.data.error.message : err.message));
            } finally {
                this.disabling = false;
            }
        },

        async createDevice() {
            this.creatingDevice = true;
            try {
                const response = await pokosmsService.createDevice();
                const result = response.data.result;
                this.qrCodeData = result.qrCodeBase64;
                this.expiresAt = result.expiresAt;
                await this.loadStatus();
            } catch (err) {
                this.error('Błąd: ' + (err.response && err.response.data && err.response.data.error ? err.response.data.error.message : err.message));
            } finally {
                this.creatingDevice = false;
            }
        },

        async refreshStatus() {
            this.refreshing = true;
            try {
                const response = await pokosmsService.refreshDeviceStatus();
                this.status = response.data.result;

                // Clear QR if device is now active
                if (this.status.deviceStatus === 'Active') {
                    this.qrCodeData = null;
                    this.success('Urządzenie zostało sparowane!');
                }
            } catch (err) {
                this.error('Błąd: ' + (err.response && err.response.data && err.response.data.error ? err.response.data.error.message : err.message));
            } finally {
                this.refreshing = false;
            }
        },

        confirmDeleteDevice() {
            this.showDeleteDeviceDialog = true;
        },

        async deleteDevice() {
            this.deletingDevice = true;
            try {
                const response = await pokosmsService.deleteDevice();
                this.success(response.data.result.message);
                this.showDeleteDeviceDialog = false;
                this.qrCodeData = null;
                await this.loadStatus();
            } catch (err) {
                this.error('Błąd: ' + (err.response && err.response.data && err.response.data.error ? err.response.data.error.message : err.message));
            } finally {
                this.deletingDevice = false;
            }
        }
    }
}
</script>

<style scoped>
ul {
    padding-left: 20px;
}
</style>
